import React, { useState, useEffect, useCallback } from "react";
import { Select } from "chakra-react-select";
import { HStack, Box, Button } from "@chakra-ui/react";
import { VendorsQuickCreate } from "../vendors/vendorQuickCreate";
import { fetchVendors } from "../../api/vendors";

// Cache with TTL to prevent stale data in long-running sessions
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let defaultVendorCache = null;
let defaultVendorCacheTimestamp = 0;
let defaultVendorCachePromise = null;
const vendorCacheMap = new Map();

const isCacheValid = () => {
  return defaultVendorCache && (Date.now() - defaultVendorCacheTimestamp) < CACHE_TTL_MS;
};

const cacheVendors = (vendors = []) => {
  vendors.forEach((vendor) => {
    if (vendor && vendor.id) {
      vendorCacheMap.set(vendor.id, vendor);
    }
  });
};

export const VendorSelect = ({
  selectedVendorID,
  onChange,
  metaData,
  w,
  menuPortalTarget,
  showIdPrefix = false,
  placeholder = "Select vendor...",
  enableQuickCreate = true,
  isClearable = false,
}) => {
  const [selected, setSelected] = useState();
  const [searchInput, setSearchInput] = useState("");
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [vendors, setVendors] = useState([]);

  const formatVendorOption = useCallback(
    (vendor) => {
      if (!vendor) {
        return vendor;
      }

      const id = vendor.id ?? vendor.value;
      const name = vendor.name ?? vendor.label ?? "";
      let label = name;

      if (showIdPrefix && id !== undefined && id !== null) {
        const idStr = String(id);
        const prefix = idStr.slice(0, 6);
        const truncated = idStr.length > 6 ? `${prefix}...` : prefix;
        label = `${name} (${truncated})`;
      }

      return {
        ...vendor,
        id,
        name,
        value: vendor.value ?? id,
        label,
      };
    },
    [showIdPrefix]
  );

  const refreshVendorsList = useCallback(() => {
    const fetchData = async () => {
      if (!searchInput && !selectedVendorID) {
        // Use cache if valid (not expired)
        if (isCacheValid()) {
          setVendors(defaultVendorCache.map((vendor) => formatVendorOption(vendor)));
          return;
        }

        if (!defaultVendorCachePromise) {
          defaultVendorCachePromise = fetchVendors({ limit: 100 }, true)
            .then((data) => {
              cacheVendors(data);
              defaultVendorCache = data;
              defaultVendorCacheTimestamp = Date.now();
              return data;
            })
            .finally(() => {
              defaultVendorCachePromise = null;
            });
        }

        const data = await defaultVendorCachePromise;
        setVendors(data.map((vendor) => formatVendorOption(vendor)));
        return;
      }

      if (selectedVendorID && vendorCacheMap.has(selectedVendorID)) {
        const cachedVendor = vendorCacheMap.get(selectedVendorID);
        const formatted = formatVendorOption(cachedVendor);
        setVendors((prev) => {
          if (prev.some((v) => v.value === formatted.value)) {
            return prev;
          }
          return [...prev, formatted];
        });
        return;
      }

      let params = { limit: 100 };
      if (searchInput) {
        params.name__icontains = searchInput;
      } else if (selectedVendorID) {
        params.id = selectedVendorID;
      }

      const data = await fetchVendors(params, true);
      cacheVendors(data);
      if (!searchInput && !selectedVendorID) {
        defaultVendorCache = data;
        defaultVendorCacheTimestamp = Date.now();
      }
      setVendors(data.map((vendor) => formatVendorOption(vendor)));
    };

    fetchData();
  }, [searchInput, selectedVendorID, formatVendorOption]);

  useEffect(() => {
    refreshVendorsList();
  }, [refreshVendorsList]);

  useEffect(() => {
    setSelected(vendors.find((a) => a.value === selectedVendorID));
  }, [vendors, selectedVendorID]);

  const onChangeLocal = (vendor) => {
    setSelected(vendor);
    onChange(vendor, metaData);
  };

  const onInputChange = (inputValue) => {
    let result = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
    setSearchInput(result);
  };

  const addVendorToList = (newVendor) => {
    const formattedVendor = formatVendorOption({
      ...newVendor,
      id: newVendor.id ?? newVendor.value,
      name: newVendor.name ?? newVendor.label,
    });

    if (formattedVendor.id) {
      vendorCacheMap.set(formattedVendor.id, formattedVendor);
    }

    setVendors((prev) => [...prev, formattedVendor]);
    setSelected(formattedVendor);
    onChange(formattedVendor, metaData);
  };

  const handleOpenQuickCreate = () => {
    setIsQuickCreateOpen(true);
  };

  const handleCloseQuickCreate = () => {
    setIsQuickCreateOpen(false);
  };

  return (
    <HStack spacing={2}>
      <Box width={w ? w : "200px"}>
        <Select
          menuPortalTarget={menuPortalTarget}
          name="vendor"
          options={vendors}
          value={selected}
          placeholder={placeholder}
          onChange={onChangeLocal}
          onInputChange={onInputChange}
          useBasicStyles
          isClearable={isClearable}
          flex="1"
          noOptionsMessage={() =>
            enableQuickCreate ? (
              <Button onClick={handleOpenQuickCreate}>Add {searchInput}</Button>
            ) : (
              "No results"
            )
          }
        />
      </Box>
      {enableQuickCreate && isQuickCreateOpen && (
        <VendorsQuickCreate
          addVendorToList={addVendorToList}
          isOpen={isQuickCreateOpen}
          onClose={handleCloseQuickCreate}
          initialName={searchInput}
        />
      )}
    </HStack>
  );
};
